/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';
import { useTranslation } from 'react-i18next';

import legalEntityCurrencyService from 'service/legalEntityCurrency';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import ProfitAndCostInfo from './ProfitAndCostInfo';
import PaymentAndTransaction from './PaymentAndTransaction';
import SalesAndGoodsInfo from './SalesAndGoodsInfo';
import SaleGraph from './SaleGraph';
import styles from './styles.module.scss';
import 'flatpickr/dist/flatpickr.css';
import legalEntityService from 'service/legalEntity';
import { useAppContext } from 'providers/AppProvider';
import { useWindowWidth } from 'utils/useWindowSize';
import DatePickerForm from 'components/forms/DatePickerForm';
import SelectForm from 'components/forms/SelectForm';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const windowSize: any = useWindowWidth();
  const {
    config: { theme }
  } = useAppContext();

  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const legalEntityId = useSelector(
    (state: any) => state?.auth?.legal_entity?.pid
  );
  const selectRef = useRef<HTMLSelectElement>(null);

  const [activeButton, setActiveButton] = useState<string | null>('Вчера');
  const [currencyOption, setCurrencyOption] = useState<any>();
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<number | null>(null);
  const [dateTo, setDateTo] = useState<number | null>(null);
  const [branchOption, setBranchOption] = useState<any>();
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');

  const dashboardBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'home_page',
      active: true
    }
  ];

  useEffect(() => {
    initializeDates();
  }, []);

  useEffect(() => {
    setDefaultBranch();
  }, [legalEntityId, branchOption]);

  useEffect(() => {
    setDefaultCurrency();
  }, [currencyId, currencyOption]);

  const initializeDates = () => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth(); // 0-based index for months
    const currentYear = today.getFullYear();

    // Check if today is the last day of the current month
    const isLastDayOfMonth =
      new Date(currentYear, currentMonth + 1, 0).getDate() === currentDay;

    // Define conditions and corresponding actions
    if (currentDay === 1) {
      // If the day is the 1st of the month, select "Today"
      handleButtonClick('Сегодня');
    } else if (currentDay === 2) {
      // If the day is the 2nd of the month, select "Yesterday"
      handleButtonClick('Вчера');
    } else if (currentMonth === 11 && isLastDayOfMonth) {
      // If it's the last day of December, select "Year"
      handleButtonClick('Год');
    } else if (currentMonth === 11) {
      // If it's any day in December (not the last), select "Month"
      handleButtonClick('Месяц');
    } else if (currentDay >= 3 && currentDay <= 10) {
      // If the day is between the 3rd and 10th, select "Week"
      handleButtonClick('Неделя');
    } else if (isLastDayOfMonth) {
      // If it's the last day of any other month, select "Year"
      handleButtonClick('Год');
    } else {
      // Default case for other days, select "Month"
      handleButtonClick('Месяц');
    }
  };

  const setDefaultBranch = () => {
    if (legalEntityId && branchOption?.length > 0) {
      const defaultBranch = branchOption.find(
        (option: any) => option.value === legalEntityId
      );
      if (defaultBranch) {
        setSelectedBranchId(defaultBranch.value);
      }
    }
  };
  const setDefaultCurrency = () => {
    if (currencyId && currencyOption?.length > 0) {
      const defaultCurrency = currencyOption.find(
        (option: any) => option.value === currencyId
      );
      if (defaultCurrency) {
        setSelectedCurrencyId(defaultCurrency.value);
      }
    }
  };

  const handleDateChange = (selectedDates: any) => {
    if (selectedDates.length === 2) {
      const startDateInMillis = new Date(selectedDates[0]).setHours(0, 0, 0, 0);
      let endDateInMillis = new Date(selectedDates[1]).setHours(0, 0, 0, 0);

      if (startDateInMillis === endDateInMillis) {
        endDateInMillis += 24 * 60 * 60 * 1000; // Add one day in milliseconds
      }
      setDateFrom(startDateInMillis);
      setDateTo(endDateInMillis);
    } else {
      setDateFrom(null);
      setDateTo(null);
    }
  };
  const handleButtonClick = (buttonLabel: string) => {
    setActiveButton(buttonLabel);
    const today = new Date();
    const startDate = new Date(today);
    const endDate = new Date(today);

    if (buttonLabel === 'Вчера') {
      startDate.setDate(today.getDate() - 1);
      // endDate.setDate(today.getDate() + 1);
    } else if (buttonLabel === 'Сегодня') {
      endDate.setDate(today.getDate() + 1);
    } else if (buttonLabel === 'Неделя') {
      startDate.setDate(today.getDate() - 7);
      endDate.setDate(today.getDate() + 1);
    } else if (buttonLabel === 'Месяц') {
      startDate.setMonth(today.getMonth() - 1);
      endDate.setDate(today.getDate() + 1);
    } else if (buttonLabel === 'Год') {
      startDate.setFullYear(today.getFullYear() - 1);
      endDate.setDate(today.getDate() + 1);
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    setDateFrom(startDate.getTime());
    setDateTo(endDate.getTime());
  };

  //GET LEgal Entity to options
  useQuery(['GET_BRANCH'], async () => {
    await legalEntityService
      .getListBranch({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entities?.map((option: any) => ({
          value: option.pid,
          label: option.name
        }));
        setBranchOption([{ value: 'all', label: t('all') }, ...options]);
      });
  });

  //GET Currency Entity to options
  useQuery(['GET_CURRENCY', i18n.language], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.pid,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  return (
    <div>
      <PageBreadcrumb items={dashboardBreadcrumbItems} />
      <div>
        <h2 className="mb-5">{t('home_page')}</h2>
        <div
          className={
            windowSize > 768
              ? 'd-flex justify-content-between gap-3 border-bottom pb-4'
              : 'd-flex flex-column gap-3 border-bottom pb-4'
          }
        >
          <div className={theme === 'dark' ? styles.listDark : styles.list}>
            <button
              className={activeButton === 'Вчера' ? styles.active : ''}
              onClick={() => handleButtonClick('Вчера')}
            >
              {t('yesterday')}
            </button>
            <button
              className={activeButton === 'Сегодня' ? styles.active : ''}
              onClick={() => handleButtonClick('Сегодня')}
            >
              {t('today')}
            </button>
            <button
              className={activeButton === 'Неделя' ? styles.active : ''}
              onClick={() => handleButtonClick('Неделя')}
            >
              {t('week')}
            </button>
            <button
              className={activeButton === 'Месяц' ? styles.active : ''}
              onClick={() => handleButtonClick('Месяц')}
            >
              {t('month')}
            </button>
            <button
              className={activeButton === 'Год' ? styles.active : ''}
              onClick={() => handleButtonClick('Год')}
            >
              {t('year')}
            </button>
          </div>

          <div className="w-100">
            <SelectForm
              options={branchOption}
              value={selectedBranchId}
              onChange={(_, selected: any) => {
                setSelectedBranchId(selected?.value);
              }}
              label={t('branch')}
            />
          </div>

          <div className="d-flex gap-2 w-100">
            <SelectForm
              options={currencyOption}
              value={selectedCurrencyId}
              onChange={(_, selected: any) => {
                setSelectedCurrencyId(selected?.value);
              }}
              label={t('currency')}
              selectRef={selectRef}
            />

            <DatePickerForm
              onChange={handleDateChange}
              mode="range"
              locale={getFlatpickrLocale()}
              placeholder={t('select_period')}
              label={t('select_period')}
            />
          </div>
        </div>

        <ProfitAndCostInfo
          selectedCurrencyId={selectedCurrencyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedBranchId={selectedBranchId}
        />
        <SaleGraph
          selectedCurrencyId={selectedCurrencyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedBranchId={selectedBranchId}
        />
        <PaymentAndTransaction
          selectedCurrencyId={selectedCurrencyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedBranchId={selectedBranchId}
        />
        <SalesAndGoodsInfo
          selectedCurrencyId={selectedCurrencyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedBranchId={selectedBranchId}
        />
      </div>
    </div>
  );
};

export default HomePage;
